import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext'

export interface AccountAdminNavDrawerOpenValue {
  drawerOpen: boolean
  setDrawerOpen: (open: boolean) => void
}

const noopSetDrawerOpen: (open: boolean) => void = () => {}

const defaultValue: AccountAdminNavDrawerOpenValue = {
  drawerOpen: false,
  setDrawerOpen: noopSetDrawerOpen,
}

const AccountAdminNavDrawerOpenContext =
  createContext<AccountAdminNavDrawerOpenValue>(defaultValue)

/**
 * Persists org-admin nav drawer open state across marketing ↔ `/account-admin` navigations (web).
 * Must render under `SupabaseAuthProvider`.
 */
export function AccountAdminNavDrawerOpenProvider({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpenState] = useState(false)
  const { isSignedIn } = useSupabaseAuth()

  useEffect(() => {
    if (!isSignedIn) {
      setDrawerOpenState(false)
    }
  }, [isSignedIn])

  const setDrawerOpen = useCallback((open: boolean) => {
    setDrawerOpenState(open)
  }, [])

  const value = useMemo(
    () => ({
      drawerOpen,
      setDrawerOpen,
    }),
    [drawerOpen, setDrawerOpen],
  )

  return (
    <AccountAdminNavDrawerOpenContext.Provider value={value}>
      {children}
    </AccountAdminNavDrawerOpenContext.Provider>
  )
}

export function useAccountAdminNavDrawerOpen(): AccountAdminNavDrawerOpenValue {
  return useContext(AccountAdminNavDrawerOpenContext)
}
